import React, { useEffect, useState } from 'react';
import { X, ArrowRight, Building2, Briefcase, Calendar, Loader2, Target } from 'lucide-react';
import { tendersAPI } from '../api/api';

// ───────────────────────────────────────────────────────────────────────────
// MatchingTendersModal — reverse view: which AOs does this consultant fit?
// Opens from the dossier editor's toolbar; closes on Esc or backdrop click.
// ───────────────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  brouillon: { label: 'Brouillon', color: '#94a3b8' },
  actif:     { label: 'Actif',     color: '#7B68EE' },
  soumis:    { label: 'Soumis',    color: '#60A5FA' },
};

const scoreColor = (s) => {
  if (s >= 75) return '#4ADE80';
  if (s >= 50) return '#FACC15';
  if (s >= 25) return '#FB923C';
  return '#F87171';
};

const ScoreBadge = ({ score, ai }) => {
  const c = scoreColor(score);
  return (
    <div style={{
      width: 44, height: 44, borderRadius: 22,
      background: `${c}1f`, color: c,
      border: `1.5px solid ${c}66`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      fontSize: 13, fontWeight: 700, lineHeight: 1,
    }}>
      {Math.round(score)}
      {ai != null && <div style={{ fontSize: 7, fontWeight: 500, opacity: 0.65 }}>IA</div>}
    </div>
  );
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const days = Math.floor((new Date(deadline) - new Date()) / 86400000);
  if (days < 0) return { label: `Dépassée ${Math.abs(days)}j`, color: '#F87171' };
  if (days === 0) return { label: "Aujourd'hui", color: '#FB923C' };
  if (days <= 7) return { label: `Dans ${days}j`, color: '#FACC15' };
  return { label: `Dans ${days}j`, color: 'var(--muted2)' };
};

const MatchingTendersModal = ({ open, dossierId, onClose }) => {
  const [tenders, setTenders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !dossierId) return;
    setLoading(true);
    setErr(null);
    tendersAPI.reverseMatch(dossierId)
      .then(setTenders)
      .catch((e) => setErr(e?.response?.data?.detail || e.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [open, dossierId]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const openTender = (id) => {
    onClose();
    window.location.assign(`/tenders/${id}`);
  };

  return (
    <div onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px',
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', color: 'var(--white)',
          borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}>
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(123,104,238,0.15)', color: 'var(--vio2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Target size={16} />
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>Appels d'offre correspondants</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted2)' }}>Top AO ouverts pour lesquels ce consultant matche</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fermer"
            style={{
              width: 32, height: 32, borderRadius: 7,
              background: 'transparent', border: '1px solid var(--border)',
              cursor: 'pointer', color: 'var(--muted2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: 'var(--muted2)' }}>
              <Loader2 size={20} className="nd-spin" />
              <style>{`@keyframes nd-spin { to { transform: rotate(360deg); } } .nd-spin { animation: nd-spin 0.9s linear infinite; }`}</style>
            </div>
          ) : err ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#F87171', fontSize: 12.5 }}>
              {err}
            </div>
          ) : !tenders || tenders.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Briefcase size={28} color="var(--muted)" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 13.5, color: 'var(--white)', fontWeight: 600, marginBottom: 4 }}>
                Aucun AO ouvert pour le moment
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted2)' }}>
                Importez un appel d'offre pour voir si ce consultant matche.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tenders.map(t => {
                const score = t.ai_score != null ? t.ai_score : t.score_keyword;
                const st = STATUS_LABELS[t.status];
                const dl = formatDeadline(t.deadline);
                return (
                  <div key={t.tender_id}
                    onClick={() => openTender(t.tender_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)', borderRadius: 10,
                      cursor: 'pointer', transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border2)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <ScoreBadge score={score} ai={t.ai_score} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--white)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.title}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: 'var(--muted2)' }}>
                        {t.client && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Building2 size={10} /> {t.client}</span>}
                        {t.sector && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Briefcase size={10} /> {t.sector}</span>}
                        {st && <span style={{ color: st.color, fontWeight: 600 }}>· {st.label}</span>}
                        {dl && <span style={{ color: dl.color, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Calendar size={10} /> {dl.label}</span>}
                      </div>
                    </div>
                    <ArrowRight size={14} color="var(--muted2)" style={{ flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchingTendersModal;
